import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PermissionService } from '../../services/permission.service';
import { MatDialog } from '@angular/material/dialog';
import { MdlManageCampaign } from '../../components/mdl-manage-campaign/mdl-manage-campaign';
import { Router } from '@angular/router';
import { Api } from '../../services/api';
import Swal from 'sweetalert2';
import { MdlRescheduleCampaign } from '../../components/mdl-reschedule-campaign/mdl-reschedule-campaign';
import { MdlChangeCampaignStatus } from '../../components/mdl-change-campaign-status/mdl-change-campaign-status';
import { AlertService } from '../../services/alert.service';

@Component({
  selector: 'app-campaigns',
  imports: [FormsModule, CommonModule],
  templateUrl: './campaigns.html',
  styleUrl: './campaigns.css'
})
export class Campaigns {
  private api = inject(Api);

  campanas: any[] = [];
  showRecentDropdown: boolean = false;
  selectedItem: string = 'todas';
  campanasFiltradas: any[] = [];
  activeFilters: any = null;
  searchTerm: string = '';
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  errorMessage: string = '';
  isLoading: boolean = true;
  openedMenuId: string | null = null;

  readonly dialog = inject(MatDialog);

  private alertService = inject(AlertService);

  constructor(
    public permissionService: PermissionService,
    private router: Router,
    private elementRef: ElementRef,
  ) {
    this.initializeFilters();
    this.cargarCampanas();
  }

  cargarCampanas(): void {
    this.api.showLoading();
    this.api.getCampanas().subscribe({
      next: (response: any) => {
        this.campanas = response.campanas;
        console.log('Campañas cargadas:', this.campanas);
        setTimeout(() => {
          this.api.hideLoading();
          this.isLoading = false;
        }, 500);
      },
      error: (error: any) => {
        setTimeout(() => {
          this.api.hideLoading();
          this.isLoading = false;
        }, 500);
        this.alertService.error('Atención', 'Error al cargar las campañas.');
      }
    });
  }

  sendCampana(campana_id: string): void {
    this.api.showLoading();
    this.api.sendCampaign({campana_id: campana_id}).subscribe({
      next: (response: any) => {
        this.api.hideLoading();
        Swal.fire({
          text: 'La campaña ha sido enviada exitosamente.',
          confirmButtonText: 'Aceptar'
        });
        this.cargarCampanas();
      },
      error: (error: any) => {
        this.api.hideLoading();
        this.alertService.error('Error al enviar', 'Error al enviar la campaña');
      }
    });
  }

  deleteCampana(campana_id: string): void {
    this.api.showLoading();
    this.api.deleteCampaign({campana_id: campana_id}).subscribe({
      next: (response: any) => {
        this.api.hideLoading();
        Swal.fire({
          text: 'La campaña ha sido eliminada exitosamente.',
          confirmButtonText: 'Aceptar'
        });
        this.cargarCampanas();
      },
      error: (error: any) => {
        this.api.hideLoading();
        this.alertService.error('Error al eliminar', 'Error al eliminar la campaña');
      }
    });
  }

  openFilterModal(): void {
    // Logic to open the filter modal
  }

  toggleRecentDropdown(): void {
    this.showRecentDropdown = !this.showRecentDropdown;
  }

  selectItem(key: string): void {
    this.selectedItem = key;
    this.showRecentDropdown = false; 
  }

  openMdlCreateCampaign(): void {
    // Logic to open the create campaign modal
  }

  private initializeFilters(): void {
    if (!this.activeFilters) {
      this.activeFilters = {
        allCompanies: true,
        allPositions: true,
        allStatus: true,
        creationDateRange: 'all',
        phoneFilter: 'all',
        emailFilter: 'all',
        companies: {},
        positions: {},
        status: {}
      };
    }
  }

  clearFilters(): void {
    this.activeFilters = null;
    this.searchTerm = '';
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.initializeFilters();
    this.campanasFiltradas = [...this.campanas];
    this.alertService.success('Filtros limpiados', 'Los filtros han sido limpiados');
  }

  hasActiveFilters(): boolean {
    if (!this.activeFilters) return false;
    
    const hasCompanyFilter = !this.activeFilters.allCompanies && 
      Object.values(this.activeFilters.companies).some(val => val === true);
    const hasPositionFilter = !this.activeFilters.allPositions && 
      Object.values(this.activeFilters.positions).some(val => val === true);
    const hasStatusFilter = !this.activeFilters.allStatus && 
      Object.values(this.activeFilters.status).some(val => val === true);
    const hasDateFilter = this.activeFilters.creationDateRange !== 'all';
    const hasPhoneFilter = this.activeFilters.phoneFilter !== 'all';
    const hasEmailFilter = this.activeFilters.emailFilter !== 'all';
    
    return hasCompanyFilter || hasPositionFilter || hasStatusFilter || hasDateFilter || hasPhoneFilter || hasEmailFilter || this.searchTerm.trim() !== '';
  }

  onSearchChange(): void {
    if (!this.searchTerm.trim()) {
      if (this.activeFilters) {
        this.applyAdvancedFilters();
      } else {
        this.campanasFiltradas = [...this.campanas];
      }
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.campanasFiltradas = this.campanas.filter(prospecto => 
        prospecto.nombre?.toLowerCase().includes(searchLower) ||
        prospecto.apellido?.toLowerCase().includes(searchLower) ||
        prospecto.cargo?.toLowerCase().includes(searchLower) ||
        prospecto.empresa?.toLowerCase().includes(searchLower) ||
        prospecto.telefono?.includes(searchLower) ||
        prospecto.email?.toLowerCase().includes(searchLower) ||
        prospecto.estado?.toLowerCase().includes(searchLower) ||
        prospecto.ejecutivo?.nombre?.toLowerCase().includes(searchLower) ||
        prospecto.ejecutivo?.apellido?.toLowerCase().includes(searchLower)
      );
    }
    
    if (this.sortColumn) {
      this.applySorting();
    }
  }

  private applyAdvancedFilters(): void {
    let filteredProspects = [...this.campanas];

    if (!this.activeFilters.allCompanies) {
      const selectedCompanies = Object.keys(this.activeFilters.companies).filter(key => this.activeFilters.companies[key]);
      if (selectedCompanies.length > 0) {
        filteredProspects = filteredProspects.filter(prospecto => 
          selectedCompanies.includes(prospecto.empresa)
        );
      }
    }

    if (!this.activeFilters.allPositions) {
      const selectedPositions = Object.keys(this.activeFilters.positions).filter(key => this.activeFilters.positions[key]);
      if (selectedPositions.length > 0) {
        filteredProspects = filteredProspects.filter(prospecto => 
          selectedPositions.includes(prospecto.cargo)
        );
      }
    }

    if (!this.activeFilters.allStatus) {
      const selectedStatus = Object.keys(this.activeFilters.status).filter(key => this.activeFilters.status[key]);
      if (selectedStatus.length > 0) {
        filteredProspects = filteredProspects.filter(prospecto => 
          selectedStatus.includes(prospecto.estado)
        );
      }
    }

    if (this.activeFilters.creationDateRange !== 'all') {
      filteredProspects = this.filterByCreationDate(filteredProspects, this.activeFilters.creationDateRange);
    }

    
    if (this.activeFilters.phoneFilter !== 'all') {
      filteredProspects = this.filterByPhone(filteredProspects, this.activeFilters.phoneFilter);
    }

    
    if (this.activeFilters.emailFilter !== 'all') {
      filteredProspects = this.filterByEmail(filteredProspects, this.activeFilters.emailFilter);
    }

    this.campanasFiltradas = filteredProspects;
    
    if (this.sortColumn) {
      this.applySorting();
    }
  }

    private filterByCreationDate(prospects: any[], dateRange: string): any[] {
    const hoy = new Date();
    
    switch (dateRange) {
      case 'today':
        return prospects.filter(prospecto => {
          if (!prospecto.fecha_creacion) return false;
          const fechaCreacion = new Date(prospecto.fecha_creacion);
          return fechaCreacion.toDateString() === hoy.toDateString();
        });
      
      case '7d':
        const sieteDiasAtras = new Date(hoy);
        sieteDiasAtras.setDate(hoy.getDate() - 7);
        return prospects.filter(prospecto => {
          if (!prospecto.fecha_creacion) return false;
          const fechaCreacion = new Date(prospecto.fecha_creacion);
          return fechaCreacion >= sieteDiasAtras && fechaCreacion <= hoy;
        });
      
      case '30d':
        const treintaDiasAtras = new Date(hoy);
        treintaDiasAtras.setDate(hoy.getDate() - 30);
        return prospects.filter(prospecto => {
          if (!prospecto.fecha_creacion) return false;
          const fechaCreacion = new Date(prospecto.fecha_creacion);
          return fechaCreacion >= treintaDiasAtras && fechaCreacion <= hoy;
        });
      
      default:
        return prospects;
    }
  }

  private filterByPhone(prospects: any[], phoneFilter: string): any[] {
    switch (phoneFilter) {
      case 'withPhone':
        return prospects.filter(prospecto => prospecto.telefono && prospecto.telefono.trim() !== '');
      case 'withoutPhone':
        return prospects.filter(prospecto => !prospecto.telefono || prospecto.telefono.trim() === '');
      default:
        return prospects;
    }
  }

  private filterByEmail(prospects: any[], emailFilter: string): any[] {
    switch (emailFilter) {
      case 'withEmail':
        return prospects.filter(prospecto => prospecto.email && prospecto.email.trim() !== '');
      case 'withoutEmail':
        return prospects.filter(prospecto => !prospecto.email || prospecto.email.trim() === '');
      default:
        return prospects;
    }
  }

  sortByColumn(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applySorting();
  }

  private applySorting(): void {
    if (!this.sortColumn) return;

    this.campanasFiltradas = [...this.campanasFiltradas].sort((a, b) => {
      let valueA = this.getSortValue(a, this.sortColumn);
      let valueB = this.getSortValue(b, this.sortColumn);

      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  private getSortValue(prospecto: any, column: string): any {
    switch (column) {
      case 'nombre':
        return `${prospecto.nombre} ${prospecto.apellido}`.toLowerCase();
      
      case 'cargo':
        return prospecto.cargo?.toLowerCase() || '';
      
      case 'empresa':
        return prospecto.empresa?.toLowerCase() || '';
      
      case 'telefono':
        return prospecto.telefono || '';
      
      case 'email':
        return prospecto.email?.toLowerCase() || '';
      
      case 'estado':
        return prospecto.estado?.toLowerCase() || '';
      
      case 'ejecutivo.nombre':
        return `${prospecto.ejecutivo?.nombre || ''} ${prospecto.ejecutivo?.apellido || ''}`.toLowerCase();
      
      default:
        return prospecto[column] || '';
    }
  }

  verDetalleCampana(campanaId: number, event: Event): void {
    event.stopPropagation();
    // Logic to navigate to campaign detail view
  }

  toggleActionsMenu(id: string): void {
    this.openedMenuId = this.openedMenuId === id ? null : id;
    this.showRecentDropdown = false; 
  }

  openManageCampana(): void {
    this.dialog.open(MdlManageCampaign, {
      minWidth: '750px',
      maxHeight: '800px',
      data: {
        mode: 'add'
      },
      panelClass: 'mat-dlg-class'
    });
  }

  openMdlDeleteCampana(campanaId: number): void {
    // Logic to open delete campaign modal
  }

  openNewChangeOwner(campana: any): void {
    // Logic to open change owner modal
  }

  openSavedSegments(): void {
    this.router.navigate(['/segments']);
  }

  openCampaignDetail(campana_id: string): void {
    this.router.navigate([`/campaign/detail/${campana_id}`]);
  }

  openCampaignSegments(campana_id: string): void {
    this.router.navigate([`/campaigns/members/${campana_id}`]);
  }

  openRescheduleCampaign(): void {
    this.dialog.open(MdlRescheduleCampaign, {
      minWidth: '700px',
      maxHeight: '800px',
      data: {
        mode: 'reschedule',
      },
      panelClass: 'mat-dlg-class'
    });
  }

  openCampaignChangeStatus(campana_id: string): void {
    console.log("Click");
    console.log('Opening Change Campaign Status modal for campaign ID:', campana_id);
    this.dialog.open(MdlChangeCampaignStatus, {
      minWidth: '500px',
      maxHeight: '800px',
      data: {
        mode: 'changeStatus',
      },
      panelClass: 'mat-dlg-class'
      
    });
  }


  @HostListener('document:click', ['$event.target'])
  onClickOutside(target: EventTarget | null): void {
      if (!(target instanceof HTMLElement)) return;
      
      const clickedInsideRecent = this.elementRef.nativeElement.querySelector('.dropdown-trigger')?.contains(target);
      const clickedInsideActions = this.elementRef.nativeElement.querySelector('.dots-button')?.contains(target);
      
      if (!clickedInsideRecent) {
          this.showRecentDropdown = false;
      }
      
      if (!clickedInsideActions) {
          this.openedMenuId = null;
      }
  }
}
